import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  HiOutlineTableCells,
  HiOutlineBolt,
  HiOutlineRocketLaunch,
  HiOutlineExclamationTriangle,
  HiOutlineLockClosed,
  HiOutlineLink,
  HiOutlineSparkles,
  HiOutlineCheck,
  HiOutlineArrowPath,
  HiOutlineArrowTopRightOnSquare,
  HiOutlineArrowRight,
  HiOutlineClipboardDocument,
  HiOutlineUsers,
} from 'react-icons/hi2';
import { useAuth } from '../../contexts/AuthContext';
import cloudApi from '../../services/cloudApi';
import analytics from '../../services/analytics';
import { usePageTitle } from '../../hooks/usePageTitle';
import { copyText } from '../../utils/clipboard';
import AppFavicon from '../../components/AppFavicon';
import { isClaudeGrant, isChatGptGrant, resolveClientIdentity } from '../../utils/mcpClients';

// OAuth consent for MCP connectors (cloud/backend/docs/integrations/mcp-server.md). The backend
// /oauth/authorize endpoint validated the request and redirected here with the raw
// query params; every decision below is re-validated server-side, so this page is
// pure presentation — it cannot mint anything on its own.
//
// The page is a small state machine over auth + plan + role (see deriveMainState):
//   consent   → a PRO workspace is selected: access level + scopes + Allow.
//   paywall   → every workspace is free and the user owns the selected one: the
//               statement paywall ("Upgrade to connect Claude"). After the
//               Upgrade click it swaps to a quiet waiting status; returning to
//               the tab re-validates and unlocks in place (see the focus effect).
//   mixed-free→ a free workspace is selected but a PRO one exists: picker keeps
//               both, the primary action becomes Upgrade instead of a dead Allow.
//   member    → the selected free workspace can only be upgraded by its owner:
//               copy-a-request-link state instead of a dead end.
//   empty     → the user somehow has zero workspaces.
// The request itself never expires server-side — only the one-time code minted at
// Approve does (60s) — so parking this tab through a signup + checkout is safe.
//
// ─── Preview mode (?preview=1) — audit any state without an AI agent ─────────
//
// Render every consent state with mock data: no OAuth request, no backend calls,
// no auth. Allow/Cancel are inert; a switcher bar at the bottom of the screen
// hops between states, so you only need to remember ONE url:
//
//   /oauth/consent?preview=1                     happy path (PRO workspace)
//   /oauth/consent?preview=1&state=mixed         PRO + free workspaces — click the
//                                                free one to see the inline upsell
//   /oauth/consent?preview=1&state=upsell        free-only → statement paywall.
//                                                Click "Upgrade to PRO" to see the
//                                                waiting state, then "Re-check
//                                                now" to simulate the upgrade
//                                                landing (toast + unlocked Allow).
//   /oauth/consent?preview=1&state=waiting       the waiting state directly
//   /oauth/consent?preview=1&state=unlocked      post-upgrade auto-unlock (toast)
//   /oauth/consent?preview=1&state=member        free workspace, member role
//   /oauth/consent?preview=1&state=empty         zero workspaces
//   /oauth/consent?preview=1&state=error         invalid-request error card
//   /oauth/consent?preview=1&state=error-limit   10-connected-apps limit banner
//
// Change the requesting app with &client=claude|chatgpt|other, or any host via
// &host=somenewagent.dev (exercises the favicon/unknown-client rendering):
//
//   /oauth/consent?preview=1&state=upsell&client=chatgpt
//   /oauth/consent?preview=1&host=agent.example.dev
//
// Preview never fires analytics. Keep PREVIEW_STATES in sync with the states
// this page can actually render — it is the audit checklist.
//
// Need the REALISTIC flow instead (real backend validation, real workspaces,
// real plan gate, a real Allow) without an AI agent? Use the URL minter:
//
//   node cloud/backend/scripts/oauth-test-url.js        # → local backend
//   node cloud/backend/scripts/oauth-test-url.js --help # all options
//
// It prints a fresh /oauth/authorize URL (valid PKCE + the RIGHT `resource`
// for that env — a URL copied from prod 400s locally because `resource` is
// byte-compared against each server's own MCP URL) plus a curl for the full
// token exchange. Requires OAUTH_ENABLED=true on the target backend.

const SCOPE_DETAILS = {
  read: {
    icon: HiOutlineTableCells,
    label: 'Read your data',
    description: 'View tables, query results, automations, and run history.',
  },
  run: {
    icon: HiOutlineBolt,
    label: 'Run automations & exports',
    description: 'Start and stop your saved automations, export tables.',
  },
  extract: {
    icon: HiOutlineRocketLaunch,
    label: 'Start new extractions',
    description: 'Launch new scraping jobs. Uses workspace credits (always with a cost confirmation).',
  },
};

const ACCESS_LEVEL_SCOPES = {
  read_only: ['read'],
  full: ['read', 'run', 'extract'],
};

// Re-validate at most this often from the focus listener (the endpoint is an
// idempotent read, this just avoids hammering it on rapid tab switches).
const RECHECK_MIN_INTERVAL_MS = 5000;

// Classify a failed validate call for the full-card error state. Returns null
// for an expired session: cloudApi's 401 handler clears the token, which flips
// isAuthenticated and triggers the /signin bounce — rendering an error card for
// that beat would just flash the wrong message.
const toValidateError = (err) => {
  if (/authentication required/i.test(err?.message || '')) return null;
  const network =
    !err?.statusCode && (err instanceof TypeError || /failed to fetch|network|load failed/i.test(err?.message || ''));
  return { message: err?.message, code: err?.code, network };
};

// ─── Preview fixtures ────────────────────────────────────────────────────────

const PREVIEW_CLIENTS = {
  claude: { clientHost: 'claude.ai', clientName: 'Claude' },
  chatgpt: { clientHost: 'chatgpt.com', clientName: 'ChatGPT' },
  other: { clientHost: 'agent.example.dev', clientName: 'Example Agent' },
};

const PREVIEW_WS = {
  paid: { id: 'ws_preview_paid', name: 'Acme Inc', role: 'owner', paidPlan: true },
  free: { id: 'ws_preview_free', name: "Maria's Workspace", role: 'owner', paidPlan: false },
  memberFree: { id: 'ws_preview_member', name: 'Design Team', role: 'member', paidPlan: false },
};

// Every state the page can render, as ?preview=1&state=<key>. `label` feeds the
// on-screen switcher; the rest seeds the page's real state variables.
const PREVIEW_STATES = {
  pro: { label: 'Happy path', workspaces: [PREVIEW_WS.paid] },
  mixed: { label: 'Mixed workspaces', workspaces: [PREVIEW_WS.paid, PREVIEW_WS.free] },
  upsell: { label: 'PRO paywall', workspaces: [PREVIEW_WS.free] },
  waiting: { label: 'Waiting for upgrade', workspaces: [PREVIEW_WS.free], upgradeClicked: true },
  unlocked: {
    label: 'Just upgraded',
    workspaces: [{ ...PREVIEW_WS.free, paidPlan: true }],
    unlockedToast: "Maria's Workspace",
  },
  member: { label: 'Member (ask owner)', workspaces: [PREVIEW_WS.memberFree] },
  empty: { label: 'No workspaces', workspaces: [] },
  error: {
    label: 'Invalid request',
    error: { message: 'The app that sent you here didn’t identify itself correctly.', code: 'invalid_client' },
  },
  'error-limit': {
    label: 'Connection limit',
    workspaces: [PREVIEW_WS.paid],
    decideError: {
      code: 'OAUTH_GRANT_LIMIT_REACHED',
      message: 'A workspace can have at most 10 connected apps. Disconnect unused ones first.',
    },
  },
};

const buildPreviewInfo = (stateDef, params) => {
  const client =
    PREVIEW_CLIENTS[params.client] || (params.host ? { clientHost: params.host, clientName: null } : PREVIEW_CLIENTS.claude);
  return {
    ...client,
    requestedScopes: ['read', 'run', 'extract'],
    workspaces: stateDef.workspaces || [],
  };
};

// ─── Small building blocks ───────────────────────────────────────────────────

const PlanBadge = ({ paid }) =>
  paid ? (
    <span className="shrink-0 px-2 py-0.5 text-[9px] font-bold tracking-wider text-white rounded-full bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500">
      PRO
    </span>
  ) : (
    <span className="shrink-0 px-2 py-0.5 text-[9px] font-bold tracking-wider text-gray-500 dark:text-zinc-400 rounded-full bg-gray-100 dark:bg-zinc-700/60 border border-gray-200 dark:border-zinc-600/40">
      FREE
    </span>
  );

const LockFooter = () => (
  <p className="mt-4 flex items-center gap-1.5 justify-center text-xs text-gray-400 dark:text-zinc-500">
    <HiOutlineLockClosed className="w-3.5 h-3.5" />
    You can disconnect this app anytime from your dashboard.
  </p>
);

const OAuthConsent = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  usePageTitle('Authorize access');

  const params = useMemo(
    () => Object.fromEntries(new URLSearchParams(location.search)),
    [location.search]
  );

  const preview = params.preview === '1';
  const previewState = preview ? PREVIEW_STATES[params.state] || PREVIEW_STATES.pro : null;

  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null); // full-card failure (validate) — {message, code, network?}
  const [decideError, setDecideError] = useState(null); // banner-level failure (decision)
  const [workspaceId, setWorkspaceId] = useState('');
  const [accessLevel, setAccessLevel] = useState('full');
  const [submitting, setSubmitting] = useState(null); // 'approve' | 'deny' | null
  const [upgradeClicked, setUpgradeClicked] = useState(false); // paywall → waiting state
  const [unlockedToast, setUnlockedToast] = useState(null); // workspace name that just went PRO
  const [planChangedNote, setPlanChangedNote] = useState(false); // stale-plan 402 race
  const [rechecking, setRechecking] = useState(false);
  const [ownerLinkCopied, setOwnerLinkCopied] = useState(false);
  const viewedRef = useRef(false);
  const upsellTrackedRef = useRef(false);
  const lastRecheckRef = useRef(0);

  // Not signed in: bounce through /signin and come back with the params intact.
  useEffect(() => {
    if (preview) return;
    if (!authLoading && !isAuthenticated) {
      navigate(`/signin?returnTo=${encodeURIComponent(`${location.pathname}${location.search}`)}`, { replace: true });
    }
  }, [preview, authLoading, isAuthenticated, navigate, location.pathname, location.search]);

  // The client only ever gets what it asked for: if it requested a narrower scope,
  // "full" is off the table (the server enforces the same ceiling).
  const fullAllowed = useMemo(() => {
    if (!info?.requestedScopes) return true;
    return ACCESS_LEVEL_SCOPES.full.every((scope) => info.requestedScopes.includes(scope));
  }, [info]);

  const pickPreferredWorkspace = useCallback((workspaces) => {
    const current = cloudApi.getCurrentWorkspaceId();
    return (
      workspaces.find((w) => w.id === current && w.paidPlan) ||
      workspaces.find((w) => w.paidPlan) ||
      workspaces[0]
    );
  }, []);

  const loadValidation = useCallback(async () => {
    const data = await cloudApi.validateOAuthRequest(params);
    setInfo(data);
    setError(null);
    const preferred = pickPreferredWorkspace(data.workspaces);
    if (preferred) setWorkspaceId((prev) => (data.workspaces.some((w) => w.id === prev) ? prev : preferred.id));
    return data;
  }, [params, pickPreferredWorkspace]);

  // Initial load — preview seeds mocks (re-runs when the switcher changes state),
  // the real flow validates against the backend.
  useEffect(() => {
    if (preview) {
      setError(previewState.error || null);
      setDecideError(previewState.decideError || null);
      setUpgradeClicked(Boolean(previewState.upgradeClicked));
      setUnlockedToast(previewState.unlockedToast || null);
      setPlanChangedNote(false);
      if (previewState.error) {
        setInfo(null);
        return;
      }
      const mock = buildPreviewInfo(previewState, params);
      setInfo(mock);
      const preferred = mock.workspaces.find((w) => w.paidPlan) || mock.workspaces[0];
      setWorkspaceId(preferred ? preferred.id : '');
      return;
    }
    if (authLoading || !isAuthenticated) return;
    let cancelled = false;
    loadValidation().catch((err) => {
      if (cancelled) return;
      const e = toValidateError(err);
      if (e) setError(e);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview, params.state, params.client, params.host, authLoading, isAuthenticated, params]);

  useEffect(() => {
    if (info && !fullAllowed) setAccessLevel('read_only');
  }, [info, fullAllowed]);

  const selectedWorkspace = info?.workspaces.find((w) => w.id === workspaceId);
  const grantedScopes = ACCESS_LEVEL_SCOPES[accessLevel];
  const hasPaidWorkspace = Boolean(info?.workspaces?.some((w) => w.paidPlan));
  const selectedIsFree = Boolean(selectedWorkspace) && !selectedWorkspace.paidPlan;
  const workspaceCount = info?.workspaces?.length || 0;

  // Which app is this, in words we can use in copy? The TLS-verified host decides;
  // the self-reported name never picks a first-class identity. Name resolution is
  // the shared util (so a client added to KNOWN_CLIENTS is named here too);
  // connectKey routes "Learn more" to the matching /connect story.
  const clientIdentity = useMemo(() => {
    const grant = { clientHost: info?.clientHost, clientId: params.client_id, clientName: info?.clientName };
    const { label } = resolveClientIdentity(grant);
    return {
      name: label === 'Unknown app' ? 'this app' : label,
      connectKey: isClaudeGrant(grant) ? 'claude' : isChatGptGrant(grant) ? 'chatgpt' : null,
    };
  }, [info, params.client_id]);

  // The main state machine (see the doc block up top).
  const mainState = useMemo(() => {
    if (!info) return null;
    if (workspaceCount === 0) return 'empty';
    if (!selectedIsFree) return 'consent';
    if (selectedWorkspace?.role !== 'owner') return 'member';
    if (!hasPaidWorkspace) return 'paywall';
    return 'mixed-free';
  }, [info, workspaceCount, selectedIsFree, selectedWorkspace, hasPaidWorkspace]);

  // ── Analytics (never in preview) ──
  useEffect(() => {
    if (preview || !info || viewedRef.current) return;
    viewedRef.current = true;
    analytics.trackClaudeConsentViewed({
      client_host: info.clientHost,
      client_name: info.clientName,
      requested_scopes: info.requestedScopes,
      workspace_count: info.workspaces?.length,
      has_paid_workspace: info.workspaces?.some((w) => w.paidPlan),
    });
  }, [preview, info]);

  useEffect(() => {
    if (preview || upsellTrackedRef.current) return;
    if (mainState !== 'paywall' && mainState !== 'mixed-free' && mainState !== 'member') return;
    upsellTrackedRef.current = true;
    analytics.capture(analytics.EVENTS.MCP_CONSENT_UPSELL_SHOWN, {
      state: mainState,
      role: selectedWorkspace?.role,
      client_host: info?.clientHost,
      free_workspaces: info?.workspaces?.filter((w) => !w.paidPlan).length,
      paid_workspaces: info?.workspaces?.filter((w) => w.paidPlan).length,
    });
  }, [preview, mainState, selectedWorkspace, info]);

  // ── Self-healing: re-validate when the tab regains focus ──
  // The upgrade happens in another tab (Stripe → /plans?checkout=success); when
  // the user comes back, this re-runs the idempotent validate call and unlocks
  // Allow in place. This effect is what makes "keep this tab open — it unlocks
  // by itself" true, so don't remove it without removing that copy.
  const recheckPlan = useCallback(
    async (trigger) => {
      if (preview) {
        // Simulate the upgrade landing, so the whole 5 → 5w → 6 transition can
        // be audited: the selected workspace flips to PRO with the toast.
        setInfo((prev) =>
          prev && {
            ...prev,
            workspaces: prev.workspaces.map((w) => (w.id === workspaceId ? { ...w, paidPlan: true } : w)),
          }
        );
        setUnlockedToast(selectedWorkspace?.name || null);
        setUpgradeClicked(false);
        return;
      }
      const now = Date.now();
      if (rechecking) return;
      // The throttle only damps rapid focus/visibility churn — an explicit
      // "Re-check now" click must always run, or the button dead-clicks right
      // after a too-early focus re-check (the moment users press it most).
      if (trigger === 'focus' && now - lastRecheckRef.current < RECHECK_MIN_INTERVAL_MS) return;
      lastRecheckRef.current = now;
      setRechecking(true);
      try {
        const wasFree = selectedIsFree;
        const data = await loadValidation();
        const selected = data.workspaces.find((w) => w.id === workspaceId) || pickPreferredWorkspace(data.workspaces);
        const unlocked = Boolean(wasFree && selected?.paidPlan);
        if (unlocked) {
          setWorkspaceId(selected.id);
          setUnlockedToast(selected.name);
          setUpgradeClicked(false);
          setPlanChangedNote(false);
        }
        analytics.capture(analytics.EVENTS.MCP_CONSENT_PLAN_RECHECK, { trigger, unlocked });
      } catch {
        // A failed re-check keeps the current state; the user can retry.
      } finally {
        setRechecking(false);
      }
    },
    [preview, rechecking, selectedIsFree, selectedWorkspace, workspaceId, loadValidation, pickPreferredWorkspace]
  );

  useEffect(() => {
    if (preview || !info) return;
    const shouldWatch = selectedIsFree || upgradeClicked || planChangedNote;
    if (!shouldWatch) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') recheckPlan('focus');
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [preview, info, selectedIsFree, upgradeClicked, planChangedNote, recheckPlan]);

  // ── Actions ──

  // Both upgrade CTAs open NEW tabs — the OAuth request lives in this tab's URL
  // and must never navigate away. `ws` pins the workspace being upgraded (the
  // plans page switches to it) and `source=consent` routes the checkout success
  // copy back here.
  const upgradeUrl = `/plans?ws=${encodeURIComponent(workspaceId)}&source=consent`;
  const learnMoreUrl = `/connect${clientIdentity.connectKey ? `?mcp=${clientIdentity.connectKey}` : ''}`;

  const handleUpgradeClick = () => {
    if (!preview) {
      analytics.capture(analytics.EVENTS.MCP_CONSENT_UPGRADE_CLICKED, {
        cta: 'upgrade',
        workspace_id: workspaceId,
        client_host: info?.clientHost,
      });
      window.open(upgradeUrl, '_blank', 'noopener');
    }
    setUpgradeClicked(true);
  };

  const handleLearnMore = () => {
    if (preview) return;
    analytics.capture(analytics.EVENTS.MCP_CONSENT_UPGRADE_CLICKED, {
      cta: 'learn_more',
      workspace_id: workspaceId,
      client_host: info?.clientHost,
    });
    window.open(learnMoreUrl, '_blank', 'noopener');
  };

  const handleCopyOwnerRequest = () => {
    // Deliberately NOT upgradeUrl: source=consent would give the OWNER a success
    // screen telling them to "go back to the tab you left open" — a tab only the
    // member has. They get the plain workspace-pinned plans link instead.
    const ownerUpgradeUrl = `/plans?ws=${encodeURIComponent(workspaceId)}`;
    const message =
      `Hi! Could you upgrade "${selectedWorkspace?.name}" to a paid plan on Ultimate Web Scraper, ` +
      `so I can connect ${clientIdentity.name}? Upgrade link: ${window.location.origin}${ownerUpgradeUrl}`;
    copyText(message).then(() => {
      setOwnerLinkCopied(true);
      setTimeout(() => setOwnerLinkCopied(false), 2000);
    });
    if (!preview) analytics.capture(analytics.EVENTS.MCP_CONSENT_ASK_OWNER_COPIED, { workspace_id: workspaceId });
  };

  const decide = async (approved) => {
    if (preview) return; // no real request to approve/deny in preview mode
    setSubmitting(approved ? 'approve' : 'deny');
    setDecideError(null);
    // Capture before the redirect: an approved decision hands the browser back to
    // the app via window.location.replace, so a post-await capture wouldn't send.
    analytics.trackClaudeConsentDecided({
      approved,
      access_level: approved ? accessLevel : undefined,
      paid_plan: selectedWorkspace?.paidPlan,
      client_host: info?.clientHost,
    });
    try {
      const result = await cloudApi.decideOAuthRequest({
        params,
        approved,
        ...(approved && { workspaceId, accessLevel }),
      });
      // Hand the browser back to the app's callback. replace() keeps this page out
      // of history so Back never re-runs a consent.
      window.location.replace(result.redirectTo);
    } catch (err) {
      // claude_consent_decided already fired optimistically (the approve path
      // redirects away on success) — record the rejection so approval rates can
      // be corrected downstream.
      analytics.capture(analytics.EVENTS.MCP_CONSENT_DECIDE_FAILED, {
        approved,
        code: err.code || (err.statusCode ? String(err.statusCode) : 'unknown'),
        client_host: info?.clientHost,
      });
      if (err.code === 'PLAN_REQUIRED') {
        // The plan check we rendered from is stale (downgraded mid-flow). Flip the
        // workspace back to free locally so the paywall state takes over — never a
        // dead Allow click — and say why.
        setInfo((prev) =>
          prev && {
            ...prev,
            workspaces: prev.workspaces.map((w) => (w.id === workspaceId ? { ...w, paidPlan: false } : w)),
          }
        );
        setPlanChangedNote(true);
      } else if (err.statusCode === 403) {
        // Membership changed mid-flow — refresh the workspace list.
        setDecideError({ message: 'You’re no longer a member of that workspace. Refreshing your workspaces…' });
        loadValidation().catch(() => {});
      } else {
        setDecideError({ message: err.message, code: err.code });
      }
      setSubmitting(null);
    }
  };

  // ─── Rendering ───────────────────────────────────────────────────────────────

  if ((authLoading && !preview) || (!info && !error)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-gray-100 to-gray-50 dark:from-black dark:via-gray-950 dark:to-black">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-9 w-9 border-b-2 border-blue-500 dark:border-cyan-400" />
          <p className="text-sm text-gray-500 dark:text-zinc-400">Checking authorization request…</p>
        </div>
      </div>
    );
  }

  const clientLabel = info?.clientHost || info?.clientName || 'An application';

  // The segmented access-level toggle — same pattern as the Integrations card
  // toggles, so it can't be confused with the workspace picker's radio cards.
  const accessToggle = (
    <>
      <label className="block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-zinc-400 mt-4 mb-1.5">
        Access level
      </label>
      <div className="flex rounded-full bg-gray-200/70 dark:bg-white/[0.06] border border-gray-300/40 dark:border-gray-700/50 p-0.5">
        {[
          { value: 'full', label: 'Full access', disabled: !fullAllowed },
          { value: 'read_only', label: 'Read-only', disabled: false },
        ].map(({ value, label, disabled }) => {
          const selected = accessLevel === value;
          return (
            <button
              key={value}
              type="button"
              onClick={() => !disabled && !submitting && setAccessLevel(value)}
              disabled={disabled || Boolean(submitting)}
              title={disabled ? 'This app requested read-only access.' : undefined}
              className={`flex-1 px-3 py-2 text-[13px] font-medium rounded-full transition-all duration-200 ${
                selected
                  ? 'bg-white dark:bg-white/[0.12] text-gray-900 dark:text-white shadow-sm font-semibold'
                  : disabled
                    ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>
      <p className="mt-1.5 px-1 text-[11.5px] text-gray-400 dark:text-zinc-500 leading-relaxed">
        {!fullAllowed
          ? `${clientIdentity.name} requested read-only access, so that’s the ceiling here.`
          : accessLevel === 'full'
            ? 'Prefer lookups only? Read-only lets it query your data — nothing runs, nothing is spent.'
            : 'Read-only: queries and analysis only. Nothing runs, nothing is spent.'}
      </p>
    </>
  );

  // Workspace control: static row for one workspace, radio cards for a few,
  // a select beyond that. The picker is local to this consent — it never
  // switches the dashboard's active workspace.
  const workspaceControl = info && workspaceCount > 0 && (
    <>
      <label className="block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-zinc-400 mb-1.5">
        Workspace
      </label>
      {workspaceCount === 1 ? (
        <div className="flex items-center gap-2.5 rounded-xl border border-gray-300 dark:border-gray-700/60 bg-white dark:bg-gray-800/60 px-3.5 py-2.5">
          <span className="flex-1 text-sm text-gray-900 dark:text-zinc-100 truncate">{selectedWorkspace?.name}</span>
          {selectedWorkspace?.role === 'member' && (
            <span className="shrink-0 px-2 py-0.5 text-[9px] font-bold tracking-wider text-amber-600 dark:text-amber-300 rounded-full bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-500/30">
              MEMBER
            </span>
          )}
          <PlanBadge paid={selectedWorkspace?.paidPlan} />
        </div>
      ) : workspaceCount <= 4 ? (
        <div className="rounded-xl border border-gray-300 dark:border-gray-700/60 overflow-hidden">
          {info.workspaces.map((w, i) => {
            const selected = w.id === workspaceId;
            return (
              <button
                key={w.id}
                type="button"
                onClick={() => !submitting && setWorkspaceId(w.id)}
                className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left transition-colors ${
                  i > 0 ? 'border-t border-gray-200 dark:border-gray-700/50' : ''
                } ${selected ? 'bg-cyan-50/60 dark:bg-cyan-500/[0.07]' : 'hover:bg-gray-50 dark:hover:bg-white/[0.03]'}`}
              >
                <span
                  aria-hidden="true"
                  className={`shrink-0 w-[15px] h-[15px] rounded-full box-border ${
                    selected ? 'border-[4.5px] border-cyan-500 bg-white' : 'border-[1.5px] border-gray-400 dark:border-zinc-600'
                  }`}
                />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium text-gray-900 dark:text-zinc-100 truncate">{w.name}</span>
                  {w.paidPlan ? (
                    <span className="block text-[11px] text-emerald-600 dark:text-emerald-400">Ready to connect</span>
                  ) : (
                    <span className="block text-[11px] text-gray-400 dark:text-zinc-500">Needs an upgrade to connect</span>
                  )}
                </span>
                {w.role === 'member' && (
                  <span className="shrink-0 px-2 py-0.5 text-[9px] font-bold tracking-wider text-amber-600 dark:text-amber-300 rounded-full bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-500/30">
                    MEMBER
                  </span>
                )}
                <PlanBadge paid={w.paidPlan} />
              </button>
            );
          })}
          {/* Free workspace selected in a mixed set → the note is a footnote of the
              list, not another look-alike card. */}
          {mainState === 'mixed-free' && (
            <div className="flex gap-2 px-3.5 py-2.5 pl-9 bg-cyan-50/60 dark:bg-cyan-500/[0.07] border-t border-dashed border-cyan-400/30">
              <HiOutlineSparkles className="w-3.5 h-3.5 text-cyan-500 dark:text-cyan-400 shrink-0 mt-0.5" />
              <p className="text-xs text-gray-500 dark:text-zinc-400 leading-relaxed">
                This workspace is on the Free plan — upgrade it to connect {clientIdentity.name}, or pick a PRO
                workspace above.{' '}
                <button type="button" onClick={handleLearnMore} className="font-semibold text-cyan-600 dark:text-cyan-400 hover:underline underline-offset-2">
                  Learn more →
                </button>
              </p>
            </div>
          )}
        </div>
      ) : (
        <>
          <select
            value={workspaceId}
            onChange={(e) => setWorkspaceId(e.target.value)}
            disabled={Boolean(submitting)}
            className="w-full rounded-xl border border-gray-300 dark:border-gray-700/60 bg-white dark:bg-gray-800/60 px-3.5 py-2.5 text-sm text-gray-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/50 focus:border-cyan-400/50 transition-all disabled:opacity-50"
          >
            {info.workspaces.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}{w.paidPlan ? '' : ' (free plan)'}{w.role === 'member' ? ' · member' : ''}
              </option>
            ))}
          </select>
          {mainState === 'mixed-free' && (
            <div className="mt-2 flex gap-2 rounded-xl border border-dashed border-cyan-400/40 bg-cyan-50/60 dark:bg-cyan-500/[0.07] px-3.5 py-2.5">
              <HiOutlineSparkles className="w-3.5 h-3.5 text-cyan-500 dark:text-cyan-400 shrink-0 mt-0.5" />
              <p className="text-xs text-gray-500 dark:text-zinc-400 leading-relaxed">
                This workspace is on the Free plan — upgrade it to connect {clientIdentity.name}, or pick a PRO workspace.
              </p>
            </div>
          )}
        </>
      )}
    </>
  );

  return (
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center px-4 py-10 bg-gradient-to-br from-gray-50 via-gray-100 to-gray-50 dark:from-black dark:via-gray-950 dark:to-black">
      {/* Ambient brand gradients — same signature as the sign-in screen */}
      <div
        className="absolute -left-24 -top-24 w-80 h-80 sm:w-96 sm:h-96 bg-blue-500/5 dark:bg-blue-500/10 blur-3xl rounded-full animate-pulse"
        aria-hidden="true"
        style={{ animationDuration: '8s' }}
      />
      <div
        className="absolute -right-24 -bottom-24 w-80 h-80 sm:w-96 sm:h-96 bg-cyan-500/5 dark:bg-cyan-500/10 blur-3xl rounded-full animate-pulse"
        aria-hidden="true"
        style={{ animationDuration: '10s', animationDelay: '1s' }}
      />

      <div className="relative w-full max-w-md">
        {/* Brand eyebrow. States that render the handshake already carry the logo
            in-card, so the eyebrow stays wordmark-only there; the paywall and the
            error card have no handshake, so the lockup brings the logo along. */}
        <div className="flex items-center justify-center gap-2.5 mb-5">
          {(!info || mainState === 'paywall') && (
            <div className="w-7 h-7 rounded-lg p-[1.5px] bg-gradient-to-br from-blue-400 via-cyan-400 to-purple-400 shadow-md shadow-cyan-500/20 shrink-0">
              <div className="w-full h-full bg-white rounded-[6px] p-[3px] flex items-center justify-center">
                <img src="/logo-128.png" alt="" className="w-full h-full" />
              </div>
            </div>
          )}
          <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-gray-500 dark:text-gray-400">
            Ultimate Web Scraper
          </p>
        </div>

        <div className="relative bg-white/90 dark:bg-gray-900/80 backdrop-blur-xl border border-gray-200/80 dark:border-gray-700/50 rounded-2xl shadow-xl dark:shadow-2xl p-6 sm:p-8">
          {/* Top accent line, echoing the sign-in dialog */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-cyan-400/60 to-transparent" aria-hidden="true" />

          {/* Post-upgrade unlock toast — the payoff of the focus re-check. */}
          {unlockedToast && info && (
            <div className="mb-5 rounded-xl border border-emerald-300 dark:border-emerald-500/40 bg-emerald-50 dark:bg-emerald-900/25 p-3 flex items-start gap-2.5">
              <span className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center shrink-0 mt-0.5">
                <HiOutlineCheck className="w-3.5 h-3.5 text-white" strokeWidth={3} />
              </span>
              <p className="text-sm text-emerald-800 dark:text-emerald-200 leading-relaxed">
                <span className="font-semibold">PRO is active on {unlockedToast}.</span> You’re good to go — just hit Allow.
              </p>
            </div>
          )}

          {/* Stale-plan note (402 race): the paywall below explains what to do. */}
          {planChangedNote && (
            <div className="mb-5 rounded-xl border border-amber-300 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-900/20 p-3 flex items-start gap-2.5">
              <HiOutlineExclamationTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-700 dark:text-amber-300 leading-relaxed">
                That workspace’s plan changed — it needs PRO to connect.
              </p>
            </div>
          )}

          {/* Handshake — our product connecting to the requesting app. The HOST stays
              the verified fact; the favicon is decoration and falls back gracefully.
              Hidden on the statement paywall, which carries its own composition. */}
          {info && mainState !== 'paywall' && (
            <div className="flex flex-col items-center text-center mb-6">
              <div className="relative flex items-center justify-center gap-3 mb-4">
                {/* Ours */}
                <div className="w-12 h-12 rounded-xl p-[1.5px] bg-gradient-to-br from-blue-400 via-cyan-400 to-purple-400 shadow-lg shadow-cyan-500/20 shrink-0">
                  <div className="w-full h-full bg-white rounded-[10px] p-1.5 flex items-center justify-center">
                    <img src="/logo-128.png" alt="Ultimate Web Scraper logo" className="w-full h-full" />
                  </div>
                </div>
                {/* Connector */}
                <div className="flex items-center" aria-hidden="true">
                  <span className="w-3 h-px bg-gradient-to-r from-transparent to-cyan-400/60" />
                  <span className="w-6 h-6 rounded-full bg-white dark:bg-gray-800 border border-cyan-400/40 flex items-center justify-center shadow-sm">
                    <HiOutlineLink className="w-3.5 h-3.5 text-cyan-500 dark:text-cyan-400" />
                  </span>
                  <span className="w-3 h-px bg-gradient-to-l from-transparent to-cyan-400/60" />
                </div>
                {/* Theirs */}
                <div className="relative shrink-0">
                  <div className="absolute -inset-1.5 bg-gradient-to-br from-blue-500/15 to-purple-500/15 rounded-2xl blur-md opacity-70" aria-hidden="true" />
                  <div className="relative w-12 h-12 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center">
                    <AppFavicon host={info?.clientHost} />
                  </div>
                </div>
              </div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-zinc-100 truncate max-w-full">
                {clientLabel}
              </h1>
              <p className="text-sm text-gray-500 dark:text-zinc-400 mt-0.5">
                {info?.clientHost && info?.clientName ? `${info.clientName} · ` : ''}wants to access your workspace
              </p>
            </div>
          )}

          {/* Full-card failure: validation failed, there is nothing to decide on. */}
          {!info && error && (
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 flex items-center justify-center">
                <HiOutlineExclamationTriangle className="w-6 h-6 text-red-500" />
              </div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-zinc-100 mb-2">
                {error.network ? 'Couldn’t reach Ultimate Web Scraper' : 'This connection request isn’t valid'}
              </h1>
              <p className="text-sm text-gray-500 dark:text-zinc-400 leading-relaxed mb-5">
                {error.network
                  ? 'Check your connection and try again — the request is still here.'
                  : 'The app that sent you here didn’t identify itself correctly, so we can’t continue safely. This usually fixes itself with a fresh start: remove the connection in your AI app and add our URL again.'}
              </p>
              {error.network ? (
                <button
                  onClick={() => loadValidation().catch((err) => { const e = toValidateError(err); if (e) setError(e); })}
                  className="w-full rounded-xl bg-gradient-to-r from-blue-400 via-cyan-400 to-purple-400 px-4 py-2.5 text-sm font-bold text-black border border-blue-300/50 shadow-lg shadow-cyan-500/20 hover:scale-[1.02] active:scale-[0.98] transition-all"
                >
                  Try again
                </button>
              ) : (
                <button
                  onClick={handleLearnMore}
                  className="w-full rounded-xl border border-gray-300 dark:border-gray-700 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-zinc-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors inline-flex items-center justify-center gap-1.5"
                >
                  Open the setup guide
                  <HiOutlineArrowTopRightOnSquare className="w-3.5 h-3.5" />
                </button>
              )}
              {!error.network && error.message && (
                <p className="mt-3 text-[11px] text-gray-400 dark:text-zinc-500 font-mono break-words leading-relaxed">{error.message}</p>
              )}
            </div>
          )}

          {/* Banner-level decision errors (grant cap etc.) — the consent stays usable. */}
          {decideError && (
            <div className="mb-5 rounded-xl border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-3.5 flex gap-2.5">
              <HiOutlineExclamationTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
              <div className="text-sm text-red-700 dark:text-red-300">
                {decideError.code === 'OAUTH_GRANT_LIMIT_REACHED' ? (
                  <>
                    <p className="leading-relaxed font-medium">This workspace already has 10 connected apps.</p>
                    <p className="leading-relaxed mt-0.5 text-red-600/80 dark:text-red-300/80">
                      Reconnecting an app you already use never counts against the limit — this only appears for a new
                      one. Disconnect an unused app, then retry here.
                    </p>
                    <a
                      href="/dashboard/integrations"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 mt-2 text-sm font-medium underline underline-offset-2"
                    >
                      Manage connected apps
                      <HiOutlineArrowTopRightOnSquare className="w-3.5 h-3.5" />
                    </a>
                  </>
                ) : (
                  <p className="leading-relaxed">{decideError.message}</p>
                )}
              </div>
            </div>
          )}

          {/* ── empty: zero workspaces (rare — the DB gives every new user one) ── */}
          {info && mainState === 'empty' && (
            <div className="text-center">
              <p className="text-sm text-gray-500 dark:text-zinc-400 leading-relaxed mb-5">
                Your account has no workspace to connect. Create one from your dashboard, then come back here and
                re-check — this request will wait.
              </p>
              <div className="flex gap-3">
                <a
                  href="/dashboard"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 rounded-xl border border-gray-300 dark:border-gray-700 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-zinc-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors inline-flex items-center justify-center gap-1.5"
                >
                  Open dashboard
                  <HiOutlineArrowTopRightOnSquare className="w-3.5 h-3.5" />
                </a>
                <button
                  onClick={() => recheckPlan('manual')}
                  className="flex-1 rounded-xl border border-cyan-400/50 bg-cyan-50 dark:bg-cyan-500/10 px-4 py-2.5 text-sm font-medium text-cyan-700 dark:text-cyan-300 hover:bg-cyan-100 dark:hover:bg-cyan-500/20 transition-colors inline-flex items-center justify-center gap-1.5"
                >
                  <HiOutlineArrowPath className={`w-4 h-4 ${rechecking ? 'animate-spin' : ''}`} />
                  Re-check
                </button>
              </div>
            </div>
          )}

          {/* ── paywall: every workspace is free, user owns the selected one.
                 The statement paywall — one message, one action. After the
                 Upgrade click the action row swaps to a quiet waiting status. ── */}
          {info && mainState === 'paywall' && (
            <div>
              {workspaceCount > 1 && <div className="mb-5">{workspaceControl}</div>}

              <span className="inline-flex items-center gap-2 rounded-full border border-cyan-500/40 dark:border-cyan-400/35 bg-cyan-50 dark:bg-cyan-500/10 px-3.5 py-1.5 mb-4">
                <HiOutlineLockClosed className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-400" />
                <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-cyan-700 dark:text-cyan-300">
                  PRO plan only
                </span>
              </span>

              <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white mb-3">
                Upgrade to connect {clientIdentity.name}
              </h1>
              <p className="text-sm text-gray-500 dark:text-zinc-400 leading-relaxed mb-6">
                {upgradeClicked
                  ? 'Finish checkout in the tab that just opened. This page unlocks by itself — nothing else to do here.'
                  : `The ${clientIdentity.name} connector is part of the PRO plan. Upgrade and you can come right back here and finish connecting.`}
              </p>

              {upgradeClicked ? (
                <div className="flex flex-wrap items-center gap-3 mb-5">
                  <span className="inline-flex items-center gap-2.5 rounded-xl border border-cyan-400/40 bg-cyan-50 dark:bg-cyan-500/10 px-4 py-3 text-sm font-medium text-cyan-700 dark:text-cyan-200">
                    <span className="relative flex h-2.5 w-2.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-60" />
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-cyan-500 dark:bg-cyan-400" />
                    </span>
                    Waiting for your upgrade…
                  </span>
                  <button
                    onClick={() => !preview && window.open(upgradeUrl, '_blank', 'noopener')}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-gray-300 dark:border-gray-700 px-4 py-3 text-sm font-medium text-gray-700 dark:text-zinc-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    Reopen checkout
                    <HiOutlineArrowTopRightOnSquare className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-3 mb-5">
                  <button
                    onClick={handleUpgradeClick}
                    className="inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-bold text-white bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 shadow-lg shadow-cyan-500/25 hover:shadow-xl hover:shadow-cyan-500/40 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
                  >
                    Upgrade to PRO
                    <HiOutlineArrowRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={handleLearnMore}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-gray-300 dark:border-gray-700 px-4 py-3 text-sm font-semibold text-gray-700 dark:text-zinc-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    Learn more
                    <HiOutlineArrowTopRightOnSquare className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}

              <div className="flex items-center gap-3 text-xs text-gray-400 dark:text-zinc-500">
                <span>
                  {upgradeClicked ? (
                    <>
                      Already paid?{' '}
                      <button
                        onClick={() => recheckPlan('manual')}
                        className="font-semibold text-cyan-600 dark:text-cyan-400 hover:underline underline-offset-2"
                      >
                        {rechecking ? 'Checking…' : 'Re-check now'}
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="font-medium text-gray-500 dark:text-zinc-400">{selectedWorkspace?.name}</span> is
                      currently on the <span className="font-medium text-gray-500 dark:text-zinc-400">Free</span> plan.
                    </>
                  )}
                </span>
                <button
                  onClick={() => decide(false)}
                  disabled={Boolean(submitting)}
                  className="ml-auto underline underline-offset-2 hover:text-gray-600 dark:hover:text-zinc-300 disabled:opacity-50"
                >
                  {submitting === 'deny' ? 'Cancelling…' : 'Cancel'}
                </button>
              </div>
            </div>
          )}

          {/* ── member: the selected free workspace can only be upgraded by its owner ── */}
          {info && mainState === 'member' && (
            <div>
              <div className="mb-5">{workspaceControl}</div>
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-amber-50 dark:bg-amber-900/25 border border-amber-200 dark:border-amber-500/30 flex items-center justify-center">
                  <HiOutlineUsers className="w-6 h-6 text-amber-500 dark:text-amber-400" />
                </div>
                <h2 className="text-lg font-bold tracking-tight text-gray-900 dark:text-white mb-2">
                  Ask your workspace owner to upgrade
                </h2>
                <p className="text-sm text-gray-500 dark:text-zinc-400 leading-relaxed max-w-xs mx-auto mb-5">
                  Connecting AI apps is a PRO feature, and{' '}
                  <span className="font-semibold text-gray-700 dark:text-zinc-200">{selectedWorkspace?.name}</span> is on
                  the Free plan. You’re a member, so upgrading is up to the workspace owner.
                </p>
                <button
                  onClick={handleCopyOwnerRequest}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-cyan-400/50 bg-cyan-50 dark:bg-cyan-500/10 px-4 py-3 text-sm font-semibold text-cyan-700 dark:text-cyan-300 hover:bg-cyan-100 dark:hover:bg-cyan-500/20 transition-colors"
                >
                  {ownerLinkCopied ? (
                    <>
                      <HiOutlineCheck className="w-4 h-4 text-emerald-500" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <HiOutlineClipboardDocument className="w-4 h-4" />
                      Copy upgrade request for your owner
                    </>
                  )}
                </button>
                <p className="mt-2 text-[11px] text-gray-400 dark:text-zinc-500">
                  A short message with the upgrade link, ready to paste into your team chat.
                </p>
                <p className="mt-4 text-xs text-gray-400 dark:text-zinc-500 leading-relaxed">
                  Once the owner upgrades, this page unlocks by itself — or just retry from {clientIdentity.name} later.
                </p>
                <button
                  onClick={() => decide(false)}
                  disabled={Boolean(submitting)}
                  className="mt-4 text-xs text-gray-400 dark:text-zinc-500 underline underline-offset-2 hover:text-gray-600 dark:hover:text-zinc-300 disabled:opacity-50"
                >
                  {submitting === 'deny' ? 'Cancelling…' : 'Cancel this request'}
                </button>
              </div>
            </div>
          )}

          {/* ── consent / mixed-free: the pick-and-allow layout. When the selected
                 workspace is free (mixed-free), the primary action becomes Upgrade
                 — Allow is never a dead click. ── */}
          {info && (mainState === 'consent' || mainState === 'mixed-free') && (
            <>
              {workspaceControl}
              {accessToggle}

              {mainState === 'consent' && (
                <div className="rounded-xl bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/50 p-3.5 mt-4 mb-6 space-y-3">
                  {grantedScopes.map((scope) => {
                    const detail = SCOPE_DETAILS[scope];
                    const Icon = detail.icon;
                    return (
                      <div key={scope} className="flex items-start gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-white dark:bg-gray-700/40 border border-gray-200 dark:border-gray-600/40 flex items-center justify-center shrink-0">
                          <Icon className="w-4 h-4 text-blue-500 dark:text-cyan-400" />
                        </div>
                        <div className="text-sm pt-0.5">
                          <span className="text-gray-800 dark:text-zinc-200">{detail.label}</span>
                          <span className="block text-xs text-gray-500 dark:text-zinc-400 leading-relaxed">{detail.description}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className={`flex gap-3 ${mainState === 'mixed-free' ? 'mt-6' : ''}`}>
                <button
                  onClick={() => decide(false)}
                  disabled={Boolean(submitting)}
                  className="flex-1 rounded-xl border border-gray-300 dark:border-gray-700 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-zinc-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors"
                >
                  {submitting === 'deny' ? 'Cancelling…' : 'Cancel'}
                </button>
                {mainState === 'mixed-free' ? (
                  <button
                    onClick={handleUpgradeClick}
                    className="flex-[1.4] inline-flex items-center justify-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 shadow-lg shadow-cyan-500/25 hover:shadow-xl hover:shadow-cyan-500/40 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
                  >
                    Upgrade to PRO
                    <HiOutlineArrowTopRightOnSquare className="w-3.5 h-3.5" />
                  </button>
                ) : (
                  <button
                    onClick={() => decide(true)}
                    disabled={Boolean(submitting) || !workspaceId}
                    className="relative group flex-1 overflow-hidden rounded-xl bg-gradient-to-r from-blue-400 via-cyan-400 to-purple-400 px-4 py-2.5 text-sm font-bold text-black shadow-lg shadow-cyan-500/20 hover:shadow-xl hover:shadow-cyan-400/30 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:hover:scale-100 disabled:cursor-not-allowed transition-all duration-300 border border-blue-300/50"
                  >
                    <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" aria-hidden="true" />
                    <span className="relative">{submitting === 'approve' ? 'Connecting…' : 'Allow access'}</span>
                  </button>
                )}
              </div>

              <LockFooter />
            </>
          )}

          {info && (mainState === 'paywall' || mainState === 'member') && <LockFooter />}

          {!info && error && !error.network && (
            <button
              onClick={() => window.close()}
              className="mt-4 w-full rounded-xl border border-gray-300 dark:border-gray-700 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-zinc-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Close this window
            </button>
          )}
        </div>
      </div>

      {/* Preview switcher — only in ?preview=1. Hops between every state this page
          can render (see the doc block up top for the full reference). */}
      {preview && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 max-w-[95vw]">
          <div className="flex items-center gap-1 flex-wrap justify-center rounded-2xl bg-gray-900/90 dark:bg-black/80 backdrop-blur border border-gray-700/60 px-3 py-2 shadow-2xl">
            <span className="text-[10px] font-bold uppercase tracking-wider text-cyan-400 mr-1.5">Preview</span>
            {Object.entries(PREVIEW_STATES).map(([key, def]) => {
              const active = (params.state || 'pro') === key;
              return (
                <button
                  key={key}
                  onClick={() => {
                    const q = new URLSearchParams(location.search);
                    q.set('state', key);
                    navigate(`${location.pathname}?${q.toString()}`, { replace: true });
                  }}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
                    active ? 'bg-cyan-500 text-black font-semibold' : 'text-zinc-300 hover:bg-white/10'
                  }`}
                >
                  {def.label}
                </button>
              );
            })}
            <span className="ml-1.5 text-[10px] text-zinc-500">buttons inert · docs in OAuthConsent.jsx</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default OAuthConsent;
